export const RTC_SAMPLE_RATE = 16000;
export const RTC_FRAME_SAMPLES = 320;
export const RTC_HEADER_BYTES = 28;
export const MAX_REWIND_SECONDS = 24 * 60 * 60;
export const NO_AUDIO_MS = 10 * 1000;
const SEEK_SETTLE_MS = 1000;
const SPEAKING_TIMEOUT_MS = 1000;

const CAPTURE_WORKLET_SOURCE = `
class RtcPcmCaptureProcessor extends AudioWorkletProcessor {
  process(inputs) {
    const input = inputs[0] && inputs[0][0];
    if (input && input.length) {
      const copy = new Float32Array(input);
      this.port.postMessage(copy, [copy.buffer]);
    }
    return true;
  }
}
registerProcessor('rtc-pcm-capture', RtcPcmCaptureProcessor);
`;

const browser = typeof window === 'undefined' ? {} : window;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/** Resamples microphone floats and emits protocol-sized PCM16 LE frames. */
export class Pcm16FrameEncoder {
  constructor(inputSampleRate, onFrame) {
    this.inputSampleRate = inputSampleRate;
    this.onFrame = onFrame;
    this.ratio = inputSampleRate / RTC_SAMPLE_RATE;
    this.buffer = new Float32Array(0);
    this.position = 0;
    this.frame = [];
  }

  push(chunk) {
    if (!chunk?.length) return;
    const input = chunk instanceof Float32Array ? chunk : new Float32Array(chunk);
    const samples = new Float32Array(this.buffer.length + input.length);
    samples.set(this.buffer);
    samples.set(input, this.buffer.length);

    while (this.position + 1 < samples.length) {
      const index = Math.floor(this.position);
      const fraction = this.position - index;
      const value = samples[index] + (samples[index + 1] - samples[index]) * fraction;
      this.frame.push(value);
      this.position += this.ratio;

      if (this.frame.length === RTC_FRAME_SAMPLES) {
        this.onFrame(this.encodeFrame(this.frame));
        this.frame = [];
      }
    }

    // Retain the last source sample for interpolation with the next audio block.
    const consumed = Math.min(Math.floor(this.position), Math.max(0, samples.length - 1));
    this.buffer = samples.slice(consumed);
    this.position -= consumed;
  }

  encodeFrame(samples) {
    const data = new ArrayBuffer(RTC_FRAME_SAMPLES * 2);
    const view = new DataView(data);
    samples.forEach((sample, index) => {
      const normalized = clamp(sample, -1, 1);
      const pcm = normalized < 0 ? normalized * 0x8000 : normalized * 0x7fff;
      view.setInt16(index * 2, Math.round(pcm), true);
    });
    return data;
  }

  reset() {
    this.buffer = new Float32Array(0);
    this.position = 0;
    this.frame = [];
  }
}

const participantId = bytes => Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');

export function parseRtcAudioFrame(data) {
  const array = data instanceof ArrayBuffer
    ? data
    : ArrayBuffer.isView(data)
      ? data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength)
      : null;
  if (!array || array.byteLength <= RTC_HEADER_BYTES) return null;

  const view = new DataView(array);
  const version = view.getUint8(0);
  const kind = view.getUint8(1);
  if (version !== 2 || (kind !== 1 && kind !== 2)) return null;

  const flags = view.getUint8(2);
  const payloadBytes = array.byteLength - RTC_HEADER_BYTES;
  const sampleCount = Math.floor(payloadBytes / 2);
  if (!sampleCount) return null;
  const samples = new Float32Array(sampleCount);
  for (let index = 0; index < sampleCount; index += 1) {
    samples[index] = view.getInt16(RTC_HEADER_BYTES + index * 2, true) / 0x8000;
  }

  return {
    version,
    kind,
    speaking: !!(flags & 1),
    replay: !!(flags & 2),
    level: view.getUint8(3),
    participantId: kind === 2 ? 'ringback' : participantId(new Uint8Array(array, 4, 16)),
    sequence: view.getUint32(20, false),
    timestamp: view.getUint32(24, false),
    samples,
  };
}

const safeDisconnect = node => {
  try { node?.disconnect?.(); } catch (_) {}
};

const stopTracks = stream => {
  stream?.getTracks?.().forEach(track => {
    try { track.stop(); } catch (_) {}
  });
};

/** Owns one user-initiated RTC connection and all browser media resources. */
export class RtcAudioClient {
  constructor({
    websocketUrl,
    startedAt,
    endedAt,
    onState = () => {},
    onPlayhead = () => {},
    onActivity = () => {},
    WebSocketClass = browser.WebSocket,
    AudioContextClass = browser.AudioContext || browser.webkitAudioContext,
    AudioWorkletNodeClass = browser.AudioWorkletNode,
    mediaDevices = browser.navigator?.mediaDevices,
    URLApi = browser.URL,
    BlobClass = browser.Blob,
    now = () => Date.now(),
  }) {
    this.websocketUrl = websocketUrl;
    this.startedAt = startedAt;
    this.endedAt = endedAt;
    this.onState = onState;
    this.onPlayhead = onPlayhead;
    this.onActivity = onActivity;
    this.WebSocketClass = WebSocketClass;
    this.AudioContextClass = AudioContextClass;
    this.AudioWorkletNodeClass = AudioWorkletNodeClass;
    this.mediaDevices = mediaDevices;
    this.URLApi = URLApi;
    this.BlobClass = BlobClass;
    this.now = now;

    this.state = { status: 'available', mic: 'off', live: true, error: null, remote: 'waiting' };
    this.socket = null;
    this.context = null;
    this.stream = null;
    this.micNodes = [];
    this.scheduledSources = new Set();
    this.nextPlaybackAt = new Map();
    this.encoder = null;
    this.workletUrl = null;
    this.micRequest = 0;
    this.lastActivityAt = 0;
    this.lastFrameAt = 0;
    this.lastAudioAt = 0;
    this.speaking = false;
    this.seekGuard = null;
    this.replayBehind = 0;
    this.started = false;
    this.terminated = false;
    this.destroyed = false;
  }

  emitState(patch) {
    if (this.destroyed) return;
    this.state = { ...this.state, ...patch };
    this.onState(this.state);
  }

  async connect() {
    if (this.started || this.destroyed || !this.websocketUrl) return;
    this.started = true;
    this.emitState({ status: 'connecting', mic: 'off', live: true, error: null });

    try {
      // Let the browser use its native output rate. Capture is resampled to the
      // protocol's 16 kHz by Pcm16FrameEncoder, just like the reference client.
      this.context = new this.AudioContextClass({ latencyHint: 'interactive' });
      if (this.context.state === 'suspended') await this.context.resume();
      if (this.destroyed || this.terminated) return;
    } catch (_) {
      if (!this.destroyed && !this.terminated) this.fail('audio');
      return;
    }

    try {
      // websocketUrl already contains the current actor id; never parse or rebuild it.
      this.socket = new this.WebSocketClass(this.websocketUrl);
      this.socket.binaryType = 'arraybuffer';
    } catch (_) {
      if (!this.destroyed && !this.terminated) this.fail('connection');
      return;
    }

    this.socket.onopen = () => {
      if (this.terminated) return;
      this.lastAudioAt = this.now();
      this.emitState({ status: 'live', live: true, error: null, remote: 'waiting' });
      this.startMicrophone();
    };
    this.socket.onmessage = event => this.handleMessage(event.data);
    // Browsers provide the useful close code/reason on the subsequent close
    // event. Do not close an already failing handshake from onerror.
    this.socket.onerror = () => {
      if (!this.terminated) this.emitState({ status: 'error', error: 'connection' });
    };
    this.socket.onclose = event => {
      if (this.terminated || this.destroyed) return;
      const failed = event?.wasClean === false || (event?.code && event.code !== 1000);
      this.closeResources(false);
      this.terminated = true;
      this.emitState({ status: failed ? 'error' : 'available', mic: 'off', error: failed ? 'connection' : null });
    };

  }

  async startMicrophone() {
    if (this.destroyed || this.terminated || !this.state.live) return;
    const request = ++this.micRequest;
    this.emitState({ mic: 'requesting' });
    if (!this.mediaDevices?.getUserMedia) {
      this.emitState({ mic: 'denied' });
      return;
    }

    let stream;
    try {
      stream = await this.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      if (this.destroyed || this.terminated || request !== this.micRequest || !this.state.live) {
        stopTracks(stream);
        return;
      }
      await this.attachMicrophone(stream, request);
    } catch (_) {
      if (!this.destroyed && !this.terminated && request === this.micRequest) {
        // Listening remains available when microphone permission is denied.
        this.stopMicrophone('denied');
      } else {
        stopTracks(stream);
      }
    }
  }

  async attachMicrophone(stream, request) {
    this.stream = stream;
    this.encoder = new Pcm16FrameEncoder(this.context.sampleRate, frame => {
      if (this.state.live && this.socket?.readyState === 1) this.socket.send(frame);
    });
    const source = this.context.createMediaStreamSource(stream);
    this.micNodes.push(source);

    if (this.context.audioWorklet?.addModule && this.AudioWorkletNodeClass && this.URLApi?.createObjectURL) {
      this.workletUrl = this.URLApi.createObjectURL(new this.BlobClass([CAPTURE_WORKLET_SOURCE], { type: 'text/javascript' }));
      await this.context.audioWorklet.addModule(this.workletUrl);
      if (this.destroyed || this.terminated || request !== this.micRequest || !this.state.live) {
        stopTracks(stream);
        return;
      }
      const capture = new this.AudioWorkletNodeClass(this.context, 'rtc-pcm-capture', {
        numberOfInputs: 1,
        numberOfOutputs: 0,
        channelCount: 1,
      });
      capture.port.onmessage = event => this.encoder?.push(event.data);
      source.connect(capture);
      this.micNodes.push(capture);
    } else {
      // Safari fallback; still emits raw PCM and never uses MediaRecorder.
      const capture = this.context.createScriptProcessor(2048, 1, 1);
      const silent = this.context.createGain();
      silent.gain.value = 0;
      capture.onaudioprocess = event => this.encoder?.push(event.inputBuffer.getChannelData(0));
      source.connect(capture);
      capture.connect(silent);
      silent.connect(this.context.destination);
      this.micNodes.push(capture, silent);
    }
    this.emitState({ mic: 'on' });
  }

  setMicrophone(enabled) {
    if (!enabled) {
      this.stopMicrophone('off');
      return;
    }
    if (this.state.status === 'live' && this.state.live) this.startMicrophone();
  }

  stopMicrophone(mic = 'off') {
    this.micRequest += 1;
    stopTracks(this.stream);
    this.stream = null;
    this.micNodes.forEach(node => {
      if ('onaudioprocess' in node) node.onaudioprocess = null;
      if (node.port) node.port.onmessage = null;
      safeDisconnect(node);
    });
    this.micNodes = [];
    this.encoder?.reset();
    this.encoder = null;
    if (this.workletUrl) {
      this.URLApi?.revokeObjectURL?.(this.workletUrl);
      this.workletUrl = null;
    }
    this.emitState({ mic });
  }

  handleMessage(data) {
    const frame = parseRtcAudioFrame(data);
    if (!frame || this.destroyed || this.terminated) return;
    const now = this.now();
    const live = !frame.replay;
    const speech = frame.kind === 1;
    if (this.seekGuard && now >= this.seekGuard.until) this.seekGuard = null;
    // Frames sent before a seek are still arriving; they must not undo it.
    if (this.seekGuard && live !== this.seekGuard.live) return;
    // Ringback is not part of the recording, so it must not end a replay.
    if (!speech && !this.state.live) return;

    const position = (frame.timestamp + frame.samples.length) / RTC_SAMPLE_RATE;
    if (speech && live !== this.state.live) {
      this.stopScheduledAudio();
      if (!live) {
        this.stopMicrophone('off');
        this.replayBehind = Math.max(0, this.durationSeconds() - position);
      }
      this.emitState({ status: live ? 'live' : 'replay', live });
    }
    if (live) this.trackRemoteAudio(speech, now);

    this.schedulePlayback(frame);
    this.lastFrameAt = now;
    if (now - this.lastActivityAt >= 100) {
      this.lastActivityAt = now;
      this.speaking = frame.speaking;
      this.onActivity({ speaking: frame.speaking, level: frame.level });
      if (speech && frame.replay && !this.seekGuard) this.onPlayhead(position, this.replayBehind);
    }
  }

  // Ringback is nobody speaking, yet it is sound, so "no audio" would be just as false.
  trackRemoteAudio(speech, now) {
    const { remote } = this.state;
    if (speech || remote !== 'audio') this.lastAudioAt = now;
    if (speech && remote !== 'audio') this.emitState({ remote: 'audio' });
    else if (!speech && remote === 'silent') this.emitState({ remote: 'waiting' });
  }

  // Runs on the hook's 1 s tick, so there is no timer of its own to clean up.
  checkRemoteAudio() {
    if (this.destroyed || this.terminated) return;
    const now = this.now();
    if (this.speaking && now - this.lastFrameAt >= SPEAKING_TIMEOUT_MS) {
      this.speaking = false;
      this.onActivity({ speaking: false, level: 0 });
    }
    if (this.state.status === 'live' && this.state.remote !== 'silent' && now - this.lastAudioAt >= NO_AUDIO_MS) {
      this.emitState({ remote: 'silent' });
    }
  }

  schedulePlayback(frame) {
    if (!this.context || this.context.state === 'closed') return;
    const buffer = this.context.createBuffer(1, frame.samples.length, RTC_SAMPLE_RATE);
    buffer.getChannelData(0).set(frame.samples);
    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.connect(this.context.destination);
    const queuedAt = this.nextPlaybackAt.get(frame.participantId) || 0;
    const startAt = Math.max(this.context.currentTime + 0.02, queuedAt);
    this.nextPlaybackAt.set(frame.participantId, startAt + buffer.duration);
    this.scheduledSources.add(source);
    source.onended = () => {
      this.scheduledSources.delete(source);
      safeDisconnect(source);
    };
    source.start(startAt);
  }

  stopScheduledAudio() {
    this.scheduledSources.forEach(source => {
      source.onended = null;
      try { source.stop(); } catch (_) {}
      safeDisconnect(source);
    });
    this.scheduledSources.clear();
    this.nextPlaybackAt.clear();
  }

  durationSeconds() {
    const start = Date.parse(this.startedAt);
    const end = this.endedAt ? Date.parse(this.endedAt) : this.now();
    return Number.isFinite(start) && Number.isFinite(end) ? Math.max(0, (end - start) / 1000) : 0;
  }

  seekToElapsed(seconds) {
    if (this.socket?.readyState !== 1) return;
    const duration = this.durationSeconds();
    const target = clamp(Number(seconds) || 0, 0, duration);
    const rewind = Math.min(MAX_REWIND_SECONDS, Math.max(0, Math.floor(duration - target)));
    const live = rewind === 0;
    // Already there: a stray press on the live handle must not mute the operator.
    if (live && this.state.live) return;
    this.stopMicrophone('off');
    this.stopScheduledAudio();
    this.socket.send(JSON.stringify({ type: 'go_live' }));
    if (rewind > 0) this.socket.send(JSON.stringify({ type: 'rewind', seconds: rewind }));
    this.seekGuard = { live, until: this.now() + SEEK_SETTLE_MS };
    this.replayBehind = rewind;
    if (live) this.lastAudioAt = this.now();
    this.emitState({ status: live ? 'live' : 'replay', live, ...(live ? { remote: 'waiting' } : {}) });
    this.onPlayhead(live ? duration : Math.max(target, duration - MAX_REWIND_SECONDS), rewind);
  }

  rewindToStart() {
    this.seekToElapsed(0);
  }

  goLive() {
    this.seekToElapsed(this.durationSeconds());
  }

  fail(error) {
    if (this.terminated || this.destroyed) return;
    this.closeResources(true);
    this.terminated = true;
    this.emitState({ status: 'error', mic: 'off', error });
  }

  closeResources(closeSocket = true) {
    const socket = this.socket;
    this.socket = null;
    if (socket) {
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      if (closeSocket && socket.readyState < 2) {
        try { socket.close(1000, 'rtc room left'); } catch (_) {}
      }
    }
    this.stopMicrophone('off');
    this.stopScheduledAudio();
    const context = this.context;
    this.context = null;
    if (context && context.state !== 'closed') {
      try { Promise.resolve(context.close()).catch(() => {}); } catch (_) {}
    }
  }

  destroy() {
    if (this.destroyed) return;
    this.terminated = true;
    this.destroyed = true;
    this.closeResources(true);
  }
}
